export type StudioDevice = {
  id: string
  name: string
  roomId: string | null
  roomName: string | null
  lastSeenAt: string | null
  isActive: boolean
}

export type RoomOption = { id: string; name: string }

export type TeacherPin = {
  staffId: string
  name: string
  hasPin: boolean
  /** No address on file means no account to attach a PIN to (D-120). */
  hasEmail: boolean
}

export type DeviceSetup = {
  devices: StudioDevice[]
  rooms: RoomOption[]
  teachers: TeacherPin[]
}

export async function fetchDeviceSetup(): Promise<DeviceSetup> {
  const res = await fetch('/api/admin/devices')
  if (!res.ok) throw new Error(`Could not load the tablets (${res.status})`)
  return res.json()
}

/** Returns the setup code, which is shown once and never retrievable again. */
export async function registerDevice(
  name: string,
  roomId: string | null,
): Promise<{ id: string; name: string; setupCode: string }> {
  const res = await fetch('/api/admin/devices', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, roomId }),
  })

  if (!res.ok) {
    const problem = await res.json().catch(() => null)
    throw new Error(problem?.error ?? `Could not add that tablet (${res.status})`)
  }

  return res.json()
}

export async function deactivateDevice(id: string): Promise<void> {
  const res = await fetch(`/api/admin/devices/${id}/deactivate`, { method: 'POST' })
  if (!res.ok) throw new Error(`Could not retire that tablet (${res.status})`)
}

export async function moveDevice(id: string, roomId: string | null): Promise<void> {
  const query = roomId ? `?roomId=${encodeURIComponent(roomId)}` : ''
  const res = await fetch(`/api/admin/devices/${id}/room${query}`, { method: 'POST' })

  if (!res.ok) throw new Error(`Could not move that tablet (${res.status})`)
}

export async function setPin(staffId: string, pin: string | null): Promise<void> {
  const res = await fetch(`/api/admin/staff/${staffId}/pin`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pin }),
  })

  if (!res.ok) {
    const problem = await res.json().catch(() => null)
    throw new Error(problem?.error ?? `Could not set that PIN (${res.status})`)
  }
}

/**
 * Why a PIN cannot be set yet, or null when it can.
 *
 * Checked on the client as well as the server so the reason is visible *beside
 * the person* rather than only after someone types four digits and presses a
 * button. The server still decides (D-73).
 */
export function pinBlockedReason(teacher: TeacherPin): string | null {
  return teacher.hasEmail
    ? null
    : 'No email address on file — add one first, so there is an account to attach the PIN to.'
}

/**
 * The same rules the server applies, so a bad PIN is refused under the keypad
 * rather than after a round trip.
 */
export function pinProblem(pin: string): string | null {
  if (pin.length < 4) return 'At least four digits.'
  if (!/^\d+$/.test(pin)) return 'Digits only — the tablet has a number pad.'

  // 0000 and 1111 are what people pick when asked for a memorable number, and
  // the first two anyone tries.
  if (new Set(pin).size === 1) return 'Pick something less guessable than one digit repeated.'

  return null
}

/**
 * "Seen 4 minutes ago" — the answer to "is the Blue Room tablet still alive?"
 * without walking to the Blue Room.
 */
export function lastSeen(at: string | null, now: Date = new Date()): string {
  if (!at) return 'Never used'

  const minutes = Math.floor((now.getTime() - new Date(at).getTime()) / 60000)

  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes} min ago`

  const hours = Math.floor(minutes / 60)
  if (hours < 24) return hours === 1 ? '1 hour ago' : `${hours} hours ago`

  const days = Math.floor(hours / 24)
  return days === 1 ? 'Yesterday' : `${days} days ago`
}
